<template>
  <SrpModal :isVisible="props.isVisible" size="large" :isClosable="false" :isCloseButtonVisible="activeForm !== AllForms.SelectHighlights" @update:isVisible="handleModalVisibilityUpdate">
    <template #insideWindow>
      <div v-if="activeForm === AllForms.SelectHighlights" class="modal-close-button edit-share-modal__select-close" @click="goBackToEditAndShare">
        <IconEmbedded class="modal-close-button__icon" name="remove_4-5" />
      </div>
    </template>

    <template #content>
      <EditAndShareForm
        v-if="activeForm === AllForms.EditAndShare"
        :restoreEditTarget="activeAssetEditTarget"
        :postToEdit="props.postToEdit"
        :customerId="props.customerId"
        :isLoadingSummaryPayload="isLoadingSummaryPayload"
        :socialShareSummaryPayload="socialShareSummaryPayload"
        :savePostCallback="savePost"
        :isSavingPost="isSavingPost"
        :customerSocialChannels="props.customerSocialChannels"
        :readonlyMode="props.readonlyMode"
        :sectionsToHide="props.sectionsToHide"
        @requestDownload="isInProgress => (isRequestingDownload = isInProgress)"
        @clickEditImages="handleClickEditImages"
        @addSocialAccount="emit('addSocialAccount')"
      />

      <PostPhotoSelectionForm
        v-if="activeForm === AllForms.SelectHighlights"
        ref="domRefPostPhotoSelectionForm"
        :customerId="props.customerId"
        :allAssetIds="socialShareSummaryPayload?.assets || []"
        :selectedAssetIds="selectedAssetIdsForEditTarget"
        :title="photosModalTitle"
        @cancel="goBackToEditAndShare"
        @done="
          assetIds => {
            setAssetIdsForTarget(props.postToEdit, activeAssetEditTarget, assetIds);
            goBackToEditAndShare();
          }
        "
      />
    </template>

    <template #footer="{ closeModal }">
      <div style="gap: 12px; display: flex; flex-wrap: wrap">
        <template v-if="activeForm === AllForms.EditAndShare">
          <SrpButton color="gray" fill="outlined" @click="closeModal(false, true)" :isDisabled="areButtonsDisabled">Cancel</SrpButton>

          <SrpButton :isDisabled="areButtonsDisabled || props.readonlyMode" @click="async () => await savePostAndClose(closeModal)">
            <template #icon>
              <LoadingIcon v-if="isSavingPost" :size="18" />
              <IconEmbedded v-else name="save_2-5" :size="18" />
            </template>
            Save
          </SrpButton>
        </template>

        <template v-if="activeForm === AllForms.SelectHighlights">
          <SrpButton color="gray" fill="outlined" @click="domRefPostPhotoSelectionForm?.cancel" :isDisabled="areButtonsDisabled">Cancel</SrpButton>

          <SrpButton @click="domRefPostPhotoSelectionForm?.done" :isDisabled="areButtonsDisabled || props.readonlyMode">
            <template #icon>
              <LoadingIcon v-if="isSavingPost" :size="18" />
              <IconEmbedded v-else name="save_2-5" :size="18" />
            </template>
            Done
          </SrpButton>
        </template>
      </div>
    </template>
  </SrpModal>
</template>

<script lang="ts">
export enum SocialPostEditAndShareFormSections {
  Caption = "caption",
  PhotosAndVideos = "photos and videos",
  CreatorTag = "creator tag",
}
</script>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import { MetricSender } from "@helpers/MetricSender";

// Components
import EditAndShareForm from "./EditAndShareForm.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import PostPhotoSelectionForm from "./PostPhotoSelectionForm.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Types
import { SocialMediaPostData, SocialShareSummaryPayload } from "@contracts/shareStop";
import { getSocialChannelLabel } from "@contracts/socialNetworks";
import { ChannelEditTarget, getAssetIdsForTarget, setAssetIdsForTarget } from "@logic/SocialPostChannelOverrides";
import type { SocialChannelData } from "@contracts/socialMediaSettings";

defineOptions({
  name: "SocialPostEditAndShareModal",
});

const props = withDefaults(
  defineProps<{
    isVisible?: boolean;
    postToEdit: SocialMediaPostData | null;
    customerId: string;
    customerSocialChannels?: Array<SocialChannelData>;
    readonlyMode?: boolean;
    sectionsToHide?: Array<SocialPostEditAndShareFormSections>;
    sourceEntityName?: string | null;
  }>(),
  {
    isVisible: false,
    postToEdit: null,
    customerId: "",
    customerSocialChannels: () => [],
    readonlyMode: false,
    sectionsToHide: () => [],
    sourceEntityName: null,
  }
);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "savePost"): void;
  (e: "addSocialAccount"): void;
}>();

// DOM refs
const domRefPostPhotoSelectionForm = ref<any>(null);
type EditTarget = ChannelEditTarget;
const activeAssetEditTarget = ref<EditTarget>("template");

// Switch between forms =======================================================
enum AllForms {
  EditAndShare,
  SelectHighlights,
}

const activeForm = ref<AllForms>(AllForms.EditAndShare);

// Is Requesting download =====================================================
const isRequestingDownload = ref<boolean>(false);

// Load socialShareSummaryPayload =============================================
const isLoadingSummaryPayload = ref<boolean>(false);
const socialShareSummaryPayload = ref<SocialShareSummaryPayload | null>(null);

async function loadShareSummaryPayload() {
  isLoadingSummaryPayload.value = true;
  socialShareSummaryPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromPostId(props.customerId, props.postToEdit?.id);
  isLoadingSummaryPayload.value = false;
}

onMounted(async () => {
  await loadShareSummaryPayload();

  if (props.postToEdit) {
    MetricSender.sendMetric("Cust-SocialPost-Open", {
      socialPostId: props.postToEdit?.id,
      aiGen: props.postToEdit?.createdWithAI,
      autoGen: props.postToEdit?.autoGenerated,
    });
  }
});

// Save post ==================================================================
const isSavingPost = ref<boolean>(false);

async function savePost() {
  if (!props.postToEdit) return;
  isSavingPost.value = true;
  await ShareStopSummaryRepo.updatePost(props.postToEdit);
  isSavingPost.value = false;

  emit("savePost");
}

async function savePostAndClose(closeModal: (isClickOnBgLayer?: boolean, isForce?: boolean) => void) {
  await savePost();
  closeModal();
}

// Are the modal buttons disabled =============================================
const areButtonsDisabled = computed(() => {
  return isLoadingSummaryPayload.value || isSavingPost.value || isRequestingDownload.value;
});

function handleClickEditImages(target: EditTarget) {
  activeAssetEditTarget.value = target;
  activeForm.value = AllForms.SelectHighlights;
}

function handleModalVisibilityUpdate(isVisible: boolean) {
  emit("update:isVisible", isVisible);
}

function goBackToEditAndShare() {
  activeForm.value = AllForms.EditAndShare;
}

const selectedAssetIdsForEditTarget = computed(() => {
  return getAssetIdsForTarget(props.postToEdit, activeAssetEditTarget.value);
});

const photosModalTitle = computed(() => {
  if (activeAssetEditTarget.value === "template") {
    return props.sourceEntityName ? `Select from "${props.sourceEntityName}"` : "Select Photos";
  }
  return `Select photos for ${getSocialChannelLabel(activeAssetEditTarget.value)}`;
});
</script>

<style scoped lang="scss">
@import "@/scss/modal-close-button.scss";

.edit-share-modal__select-close {
  position: absolute;
  inset: -8px -8px auto auto;
  z-index: 12;
}
</style>
